import { documentTaxSchema } from "./tax";
import { valueType } from "./global";
import { z } from "zod";

// =============================================================================
// LEGACY SCHEMAS (kept for backward compatibility with forms)
// =============================================================================

export const lineItemTaxSchema = z.object(
  {
    id: z.string(),
    taxDesignation: z.string(),
    taxRate: z.number(),
    taxValue: z.number(),
    cumulativeTaxes: z.array(z.lazy((): z.ZodTypeAny => lineItemTaxSchema)).optional(),
  },
  { invalid_type_error: "Line item tax must be an object" },
);

export const lineItemSchema = z.object(
  {
    itemId: z.string().optional().nullable(),
    name: z
      .string({ invalid_type_error: "Item name must be a string" })
      .min(1, { message: "Item name cannot be empty" }),
    description: z
      .string({
        invalid_type_error: "Item description must be a string",
      })
      .optional()
      .nullable(),
    quantity: z.coerce
      .number({ invalid_type_error: "Quantity must be a number" })
      .positive({ message: "Quantity must be positive" })
      .min(1, { message: "Quantity must be at least 1" }),
    netPrice: z.coerce
      .number({ invalid_type_error: "Net price must be a number" })
      .min(0, { message: "Net price must be positive" }),
    costPrice: z.coerce
      .number({ invalid_type_error: "Cost price must be a number" })
      .positive({ message: "Cost price must be positive" })
      .or(z.literal(0))
      .optional()
      .nullable(),
    grossPrice: z.coerce
      .number({ invalid_type_error: "Gross price must be a number" })
      .min(0, { message: "Gross price must be positive" }),
    discount: z.coerce
      .number({ invalid_type_error: "Discount must be a number" })
      .min(0, { message: "Discount cannot be negative" })
      .optional()
      .nullable(),
    discountType: valueType,
    taxIds: z.array(z.string()).optional().nullable(),
    taxes: z.array(lineItemTaxSchema).optional().nullable(),
  },
  { invalid_type_error: "Item must be an object" },
);

// Export types
export type LineItemSchema = z.infer<typeof lineItemSchema>;
export type LineItemTaxSchema = z.infer<typeof lineItemTaxSchema>;

// =============================================================================
// SHARED DOCUMENT FIELDS SCHEMA (corresponds to documentFields in DB)
// =============================================================================

/**
 * Document Fields Schema
 * Shared by all document types (invoices, estimates, credit notes, expenses)
 * Corresponds to `documentFields` in database schema
 */
export const sharedDocumentFieldsSchema = z.object({
  // Organization Details
  organizationName: z.string(),
  organizationAddressLine1: z.string().optional().nullable(),
  organizationAddressLine2: z.string().optional().nullable(),
  organizationZipcode: z.string().optional().nullable(),
  organizationState: z.string().optional().nullable(),
  organizationCity: z.string().optional().nullable(),
  organizationCountry: z.string().optional().nullable(),
  organizationLogo: z.string().optional().nullable(),

  // Relation Details
  relationId: z.string().min(1, { message: "Relation is required" }),
  relationName: z.string(),
  relationAddressLine1: z.string().optional().nullable(),
  relationAddressLine2: z.string().optional().nullable(),
  relationZipcode: z.string().optional().nullable(),
  relationState: z.string().optional().nullable(),
  relationCity: z.string().optional().nullable(),
  relationCountry: z.string().optional().nullable(),

  // Common Fields
  currency: z.string(),
  exchangeRates: z.record(z.string(), z.number()).nullable(),
  date: z.date(),
  showItemsWithTax: z.boolean(),
  subtotal: z.number(),
  total: z.number(),
  items: z.array(lineItemSchema),
  taxes: z.array(documentTaxSchema),
});

export type SharedDocumentFieldsSchema = z.infer<typeof sharedDocumentFieldsSchema>;
